import { action, computed, observable } from 'mobx';
import Model from 'core/model/Model';
import api from 'core/util/api';
import $auth from 'app/stores/$auth';
import $devices from 'app/stores/device/$devices';
import $kentikAgents from 'app/stores/kentikAgent/$kentikAgents';
import $setup from 'app/stores/$setup';
import $sites from 'app/stores/site/$sites';
import $labels from 'app/stores/label/$labels';
import $metrics from 'app/stores/metrics/$metrics';
import $globalFilters from 'app/stores/globalFilter/$globalFilters';
import { ApiBatcher } from 'app/stores/util/apiBatcher';
import { showSuccessToast } from 'core/components';
import MetricDeviceModel from 'app/stores/metrics/MetricDeviceModel';
import { DEVICE_CPU_QUERY_SERIES } from 'app/views/metrics/queries';
import * as deviceDerived from '@kentik/ui-shared/device/deviceDerivedFields';
import {
  ESSENTIALS_FEATURE_FLAG,
  getEffectiveDefaultPresetId,
  MODERN_MONITORING_TEMPLATE_FORM_FEATURE_FLAG
} from '@kentik/ui-shared/nms/deviceMonitoringTemplates';
import { GROUP_ID_PREFIX } from '@kentik/ui-shared/agents/constants';
import { DeviceClassToCapabilities } from 'app/util/nms/deviceMetadata';
import { hasRequiredLabel, requiredDeviceLabels } from 'app/stores/auth/AuthRbacLabels';
import { DeviceSyslogsStatus } from 'app/stores/device/DeviceSyslogsStatus';
import { DeviceSyslogsCollection } from 'app/stores/device/DeviceSyslogsCollection';
import { $metricsDevices } from 'app/stores/metrics/$metricsDevices';
import { deviceIndex } from 'app/util/lookups/deviceIndex';
import { DeviceTrapsStatus } from 'app/stores/device/DeviceTrapsStatus';
import { DeviceTrapsCollection } from 'app/stores/device/DeviceTrapsCollection';

const flowFetch = new ApiBatcher({ url: '/api/ui/devices/batch/flow', batchWindow: 100 });

export const fetchDeviceHasFlow = (deviceId) => flowFetch.fetch(Number.parseInt(deviceId)).then((result) => !!result);

$globalFilters.registerScopeChangeHandler(() => flowFetch.clear());

class DeviceModel extends Model {
  /**
   * @type {import('app/stores/metrics/MetricDeviceModel').default | null}
   */
  @observable.ref
  metricsDevice = null;

  @observable.ref
  connections = null;

  @observable
  hasFetched = false;

  @observable
  _flowLoading = true;

  @observable
  _hasFlow = false;

  _telemetryLoading;

  _componentsLoading;

  _isisAdjacenciesLoading;

  _bgpNeighborsLoading;

  @observable
  fetchedTelemetry = false;

  @computed
  get syslogs() {
    return new DeviceSyslogsCollection(this);
  }

  @computed
  get loadingSyslogStatus() {
    return !!DeviceSyslogsStatus.fetchPromise && !DeviceSyslogsStatus.fetched;
  }

  // Note that you need to manually trigger `getDeviceSyslogsStatus` for this to work
  @computed
  get hasSyslogs() {
    if (DeviceSyslogsStatus.fetched) {
      return DeviceSyslogsStatus.lastFetched[this.id] || null;
    }
    if (!DeviceSyslogsStatus?.fetchPromise) {
      console.warn('Attempted to display syslogs status for device prior to triggering API fetch!');
    }
    return null;
  }

  @action
  async fetchSyslogsStatus() {
    return DeviceSyslogsStatus.getDeviceSyslogsStatus();
  }

  @computed
  get traps() {
    return new DeviceTrapsCollection(this);
  }

  @computed
  get loadingTrapStatus() {
    return !!DeviceTrapsStatus.fetchPromise && !DeviceTrapsStatus.fetched;
  }

  // Note that you need to manually trigger `getDeviceTrapsStatus` for this to work
  @computed
  get hasTraps() {
    if (DeviceTrapsStatus.fetched) {
      return DeviceTrapsStatus.lastFetched[this.id] || null;
    }
    if (!DeviceTrapsStatus?.fetchPromise) {
      console.warn('Attempted to display traps status for device prior to triggering API fetch!');
    }
    return null;
  }

  @action
  async fetchTrapsStatus() {
    return DeviceTrapsStatus.getDeviceTrapsStatus();
  }

  @computed
  get canUpdate() {
    return (
      $auth.hasRbacPermissions(['device::update']) && hasRequiredLabel(requiredDeviceLabels.get().update, this.labels)
    );
  }

  @computed
  get canDelete() {
    return (
      $auth.hasRbacPermissions(['device::delete']) && hasRequiredLabel(requiredDeviceLabels.get().delete, this.labels)
    );
  }

  /**
   * You shouldn't have to call this directly.
   *
   * @returns {Promise<boolean>}
   * @private
   */
  @action
  async _fetchFlow() {
    const result = await flowFetch.fetch(Number.parseInt(this.id));
    this._flowLoading = false;
    this._hasFlow = !!result;
    return this._hasFlow;
  }

  @action
  async loadDeviceStatus() {
    return api.get(`/api/ui/devices/status/${this.id}`).then((response) => {
      const status = response[this.id];

      if (status) {
        this.set(status);
      }

      return status;
    });
  }

  /**
   * Loads the config status for this device if configs are enabled.
   * Sets the configStatus attribute with revision info, fetch status, etc.
   * @returns {Promise<void>}
   */
  @action
  async loadConfigStatus() {
    if (!this.hasConfigs) {
      return Promise.resolve();
    }

    try {
      const response = await api.post('/api/ui/devices/configs?includeData=false', {
        data: { deviceIds: [this.id] }
      });

      const deviceConfig = response[this.id];
      if (deviceConfig) {
        const configData = {
          revisionId: deviceConfig.revision?.id,
          firstFetched: deviceConfig.revision?.first_fetched,
          lastFetched: deviceConfig.revision?.last_fetched,
          configSize: deviceConfig.config_data?.length || undefined,
          fetchError: deviceConfig.fetch_error ? deviceConfig.config_data : false
        };
        this.set('configStatus', configData);
        return configData;
      }
      return null;
    } catch (error) {
      console.error(`Failed to fetch config status for device ${this.id}:`, error);
      this.set('configStatus', { fetchError: true });
      throw error;
    }
  }

  @computed
  get hasFlow() {
    if (!this._flowLoading) {
      // expected response
      return this._hasFlow;
    }
    // trigger flow fetch if necessary (we can use FPS data here if it's already loaded!)
    this._fetchFlow();

    // null value means that it's still loading, you can check for this condition or just treat it as falsy
    return this.rawFps > 0 || null;
  }

  @computed
  get urlRoot() {
    return '/api/ui/devices';
  }

  get defaults() {
    return {
      device_snmp_v3_conf_enabled: false,
      sending_ips: [''],
      cdn_attr: 'N',
      labels: [],
      bgpPeerIP4: null,
      bgpPeerIP6: null,
      minimize_snmp: false,
      bgp_enabled: true,
      device_bgp_type: 'device',
      device_gnmi_v1_conf: null
    };
  }

  get omitDuringSerialize() {
    return ['all_interfaces', 'metricsDevice', 'plan', 'fps5m'];
  }

  @computed
  get percentClassified() {
    return deviceDerived.percentClassified(this.attributes);
  }

  @action
  fetchTelemetry() {
    if (!this._telemetryLoading) {
      this._telemetryLoading = api
        .get(`/api/ui/devices/telemetry/${this.id}`)
        .then((telemetry) => {
          this.fetchedTelemetry = true;
          this.metricsDevice?.set(telemetry);
        })
        .catch((error) => {
          // Reset loading flag on error so callers can retry
          this._telemetryLoading = undefined;
          // Optional: surface error for upstream handling
          throw error;
        });
    }

    return this._telemetryLoading;
  }

  @action
  fetchComponents() {
    if (!this._componentsLoading) {
      this._componentsLoading = api
        .get(`/api/ui/devices/components/${this.id}`)
        .then((components) => {
          this.metricsDevice?.loadComponents(components);
        })
        .catch((error) => {
          // Reset loading flag on error so callers can retry
          this._componentsLoading = undefined;
          throw error;
        });
    }

    return this._componentsLoading;
  }

  @action
  fetchIsisAdjacencies() {
    if (!this._isisAdjacenciesLoading) {
      this._isisAdjacenciesLoading = api
        .get(`/api/ui/devices/isis-adjacencies/${this.id}`)
        .then((isisAdjacencies) => {
          this.metricsDevice?.loadIsisAdjacencies(isisAdjacencies);
        })
        .catch((error) => {
          // Reset loading flag on error so callers can retry
          this._isisAdjacenciesLoading = undefined;
          throw error;
        });
    }

    return this._isisAdjacenciesLoading;
  }

  @action
  fetchBgpNeighbors() {
    if (!this._bgpNeighborsLoading) {
      this._bgpNeighborsLoading = api
        .get(`/api/ui/devices/bgp-neighbors/${this.id}`)
        .then((neighbors) => {
          this.metricsDevice?.loadNeighbors(neighbors);
        })
        .catch((error) => {
          // Reset loading flag on error so callers can retry
          this._bgpNeighborsLoading = undefined;
          throw error;
        });
    }

    return this._bgpNeighborsLoading;
  }

  async savePartial() {
    return this.save(
      { device_status: this.get('device_status') || 'INI' },
      { url: '/api/ui/devices/partial', toast: false }
    );
  }

  saveComplete() {
    const url = this.isNew && this.get('device_status') !== 'INI' ? '/api/ui/devices/' : '/api/ui/devices/complete';
    return this.save({}, { url, toast: false });
  }

  serialize(data) {
    const site_id = data.site?.id ?? data.site_id;
    const plan_id = data.plan?.id ?? data.plan_id;

    if (!data.snmp_collection_method?.includes('kproxy') && !data.snmp_collection_method?.includes('direct_to_saas')) {
      data.device_snmp_ip = null;
      data.device_snmp_community = '';
      data.device_snmp_v3_conf_enabled = false;
    }

    data.device_gnmi_v1_conf = data.streaming_telemetry_collection_method?.includes('kproxy')
      ? { ...data.device_gnmi_v1_conf, dialoutServer: 'auto' }
      : null;

    // we want to store ST timeout as Go duration, ie "20s"
    if (data.streaming_telemetry_collection_options?.timeout) {
      data.streaming_telemetry_collection_options.timeout = `${data.streaming_telemetry_collection_options.timeout}s`;
    }

    const device_snmp_v3_conf =
      data.device_snmp_v3_conf_enabled && data.device_snmp_v3_conf.UserName.trim() ? data.device_snmp_v3_conf : null;

    if (data.bgp_enabled === false) {
      data.device_bgp_type = 'none';
    } else if (data.bgp_enabled === true) {
      if (data.device_bgp_type === 'other_device') {
        delete data.device_bgp_neighbor_ip;
        delete data.device_bgp_neighbor_ip6;
      }
      if (data.device_bgp_neighbor_ip || data.device_bgp_neighbor_ip6) {
        data.device_bgp_type = 'device';
      } else if (data.use_bgp_device_id) {
        data.device_bgp_type = 'other_device';
      }
    }

    if (data.device_bgp_type === 'device' || data.device_bgp_type === 'none') {
      data.use_bgp_device_id = null;
    }

    if ($devices.getDeviceTypeFromSubtype(data.device_subtype) !== 'host-nprobe-dns-www') {
      delete data.cdn_attr;
    }

    if (data.device_bgp_type !== 'device') {
      data.device_bgp_neighbor_ip = null;
      data.device_bgp_neighbor_ip6 = null;
      data.device_bgp_neighbor_asn = null;
      data.device_bgp_password = null;
      data.device_bgp_flowspec = false;
      data.device_bgp_label_unicast = false;
      data.bgpPeerIP4 = null;
      data.bgpPeerIP6 = null;
    }

    if (data.device_bgp_type !== 'none') {
      data.use_asn_from_flow = false;
    }

    if (data.interfaceCollection) {
      delete data.interfaceCollection;
    }

    return super.serialize({ ...data, site_id, plan_id, device_snmp_v3_conf });
  }

  deserialize(data) {
    if (data && data[this.jsonRoot]) {
      data = super.deserialize(data);
    }

    if (data) {
      // Preserve deviceconf_options if the incoming summary data doesn't include it
      if (!data.deviceconf_options && this.get('deviceconf_options')) {
        data.deviceconf_options = this.get('deviceconf_options');
      }
      // Flatten fetch_enabled to a top-level observable field so MobX tracks it correctly
      if (data.deviceconf_options !== undefined) {
        data.configFetchEnabled = !!data.deviceconf_options?.fetch_enabled;
      }
      data.bgp_enabled = data.device_bgp_type !== 'none';

      if (data.device_bgp_neighbor_ip && data.device_bgp_neighbor_ip6) {
        data.bgp_ip_type = 'both';
      } else if (data.device_bgp_neighbor_ip6) {
        data.bgp_ip_type = 'ipv6';
      } else {
        data.bgp_ip_type = 'ipv4';
      }
    }

    // remove the "s" from the end of the durations
    if (data.streaming_telemetry_collection_options) {
      const { timeout } = data.streaming_telemetry_collection_options;
      data.streaming_telemetry_collection_options.timeout = timeout?.replace(/s$/, '');
    }

    if (data.metricsDevice?.id) {
      this.metricsDevice ??= new MetricDeviceModel();
      this.metricsDevice.set(this.metricsDevice.deserialize(data.metricsDevice));
      this.metricsDevice.set({
        componentCount: data.component_count,
        neighborCount: data.neighbor_count,
        adjacencyCount: data.adjacency_count,
        neighborNotEstablishedCount: data.neighbor_nonestablished_count
      });
    }

    return data;
  }

  // drops cached point lookups for this device so labels/links reflect a save, archive, delete or restore
  invalidateLookups() {
    deviceIndex.invalidate(this.id);
  }

  @action
  async save(attributes = {}, options = {}) {
    const { isNew } = this;

    return super.save(attributes, options).then((result) => {
      this.invalidateLookups();
      $setup.getPlans({ force: true });

      if (this.get('device_status') !== 'INI') {
        $devices.loadDeviceSummaries();
        $labels.refreshActiveDeviceLabels();
      }

      if (isNew && this.isNMS && $metricsDevices.collection.hasFetched) {
        $metricsDevices.collection.fetch({ force: true });
      }

      return result;
    });
  }

  @action
  async unarchive(options = {}) {
    const { toast = true, url = `${this.url}/unarchive` } = options;

    this.requestStatus = 'updating';

    return api.put(url).then(
      () => {
        this.set('device_status', 'V');
        this.invalidateLookups();
        $labels.refreshActiveDeviceLabels();
        this.requestStatus = null;

        if (toast) {
          showSuccessToast(`Device ${this.get('device_name')} was restored successfully`);
        }

        return true;
      },
      (error) => {
        this.error = { label: 'updating' };
        this.requestStatus = null;
        throw error;
      }
    );
  }

  /**
   * @param [options]
   * @returns {Promise<undefined>}
   */
  @action
  async fetch(options = {}) {
    if (!this.hasFetched || options.force) {
      return super.fetch(options).then(() => {
        this.hasFetched = true;
      });
    }
    return Promise.resolve();
  }

  @action
  async destroy(options) {
    const { isActive } = this;

    return super.destroy({ remove: !isActive, ...options }).then((result) => {
      this.invalidateLookups();
      $labels.refreshActiveDeviceLabels();

      return $devices.loadDeviceSummaries().then(() => {
        if (isActive) {
          this.set('device_status', 'D');
        } else {
          // force sites to refresh to account for an associated device being removed
          // TODO probably redundant now that loadDeviceSummaries refreshes sites itself
          $sites.collection.resetFetchCache();
        }

        return result;
      });
    });
  }

  get sortValues() {
    return {
      id: () => parseInt(this.get('id')),
      sending_ips: () => this.sendingIpSortValue ?? 'zzz',
      deviceType: () => this.deviceType.toLowerCase(),
      site_name: () => (this.get('site_name') ? this.get('site_name').toLowerCase() : 'zzzzzz'),
      'stats.RibPrefixes': () => this.get('stats.RibPrefixes') || 0 + this.get('stats.RibPrefixes6') || 0,
      configs: () => {
        if (!this.hasConfigFetch) {
          return 'zzz'; // Devices without configs sort last
        }
        const configStatus = this.get('configStatus');
        if (!configStatus) {
          return 'b'; // Warning: no data
        }
        if (configStatus.fetchError) {
          return 'a'; // Error: sorts first
        }
        // Success: sort by last fetched time (most recent first, use negative timestamp)
        const lastFetched = configStatus.lastFetched?.seconds || 0;
        return `c${String(9999999999 - lastFetched).padStart(10, '0')}`;
      }
    };
  }

  get messages() {
    return {
      create: `Device ${this.get('device_name')} was added successfully`,
      update: `Device ${this.get('device_name')} was updated successfully`,
      destroy: `Device ${this.get('device_name')} was removed successfully`
    };
  }

  @computed
  get displayedMonitoringTemplateId() {
    const hasEssentialsPermission =
      $auth.hasPermission(ESSENTIALS_FEATURE_FLAG, { overrideForSudo: false }) ||
      $auth.hasPermission(MODERN_MONITORING_TEMPLATE_FORM_FEATURE_FLAG, { overrideForSudo: false });
    const defaultPresetId = getEffectiveDefaultPresetId(hasEssentialsPermission);
    const defaultTemplate = $metrics.monitoringTemplatesCollection.unfiltered.find(
      (model) => model.get('preset') === defaultPresetId
    );
    return deviceDerived.effectiveMonitoringTemplateId(this.attributes, defaultTemplate?.id);
  }

  /**
   * Returns us the number of Interfaces that were Classified / Matched by a particular Rule. Need this at the
   * device level to show breakdowns in the Rule Details Dialog when testing Rules against Devices.
   */
  interfacesMatchedByRule = (rule) => {
    const rules = this.get('rules');

    if (rule && rules) {
      return rules.find((r) => r.id === rule.id);
    }

    return null;
  };

  /**
   * @returns {number[]}
   */
  @computed
  get labels() {
    if (this.has('labels')) {
      return $labels.getLabelsById(this.get('labels').map((label) => label.id));
    }
    return $labels.getLabels('device', this.id);
  }

  // we use this field to allow searching by label
  @computed
  get appliedLabels() {
    return this.labels.length > 0 ? this.labels.map((label) => label.get('name')).join(' ') : 'no labels';
  }

  @computed
  get hasVendor() {
    return !!this.get('device_vendor_type') || !!this.get('vendor');
  }

  @computed
  get vendor() {
    return this.get('device_vendor_type') || this.get('vendor') || this.get('device_model_type') || this.get('model');
  }

  @computed
  get temperature_last() {
    return this.get('temperature')?.last_instant;
  }

  @computed
  get traffic() {
    const traffic_timeseries = this.get('traffic_timeseries') || [];
    return traffic_timeseries[traffic_timeseries.length - 1];
  }

  @computed
  get isUnobservable() {
    return this.get('nms_status') === 'unobservable';
  }

  @computed
  get statusDisplay() {
    return deviceDerived.statusDisplay(this.attributes);
  }

  @computed
  get statusIntent() {
    const status = this.get('nms_status');

    if (!status) {
      return 'none';
    }

    const statusIntent = {
      down: 'danger',
      up: 'success',
      unknown: 'none',
      unobservable: 'warning',
      possibly_down: 'warning'
    };

    return statusIntent[status];
  }

  @computed
  get statusRaw() {
    const { bgpStatus, downSampledFps } = this;
    const flowStatusRaw = downSampledFps > 0 ? 0 : 1;

    const snmpStatusRaw = this.get('snmp') ? 0 : 1;

    let bgpStatusRaw;
    if (bgpStatus.v4.established || bgpStatus.v6.established) {
      bgpStatusRaw = 0;
    } else if (!bgpStatus.v4.configured && !bgpStatus.v6.configured) {
      bgpStatusRaw = 1;
    } else {
      bgpStatusRaw = 2;
    }

    return flowStatusRaw + snmpStatusRaw + bgpStatusRaw;
  }

  /**
   * Note that this can be somewhat expensive on KDE. The advantage is that it gives a result which is extremely recent (unless local caching gets in the way)
   * @returns {number|null}
   */
  @computed
  get rawFps() {
    return deviceDerived.rawFps(this.attributes);
  }

  /**
   * Note that this can be somewhat expensive on KDE. The advantage is that it gives a result which is extremely recent (unless local caching gets in the way)
   * @returns {number|null}
   */
  @computed
  get downSampledFps() {
    const fps = Number(this.get('downsampled_fps'));
    return Number.isNaN(fps) ? 0 : fps;
  }

  @computed
  get flowType() {
    return deviceDerived.flowType(this.attributes);
  }

  @computed
  get isActive() {
    return this.get('device_status') === 'V';
  }

  @computed
  get isArchived() {
    return this.get('device_status') === 'D';
  }

  @computed
  get isIncomplete() {
    return this.get('device_status') === 'INI';
  }

  @computed
  get flowSource() {
    return deviceDerived.flowSource(this.attributes);
  }

  @computed
  get bgpConfigured() {
    const { v4, v6 } = this.bgpStatus;

    return v4.configured || v6.configured;
  }

  @computed
  get hasLabels() {
    return this.get('labels') && this.get('labels').length > 0;
  }

  @computed
  get deviceType() {
    return deviceDerived.deviceType(this.attributes);
  }

  @computed
  get sendingIpsDisplay() {
    return this.get('sending_ips') && this.get('sending_ips').join(', ');
  }

  @computed
  get sendingIpSortValue() {
    return deviceDerived.sendingIpSortValue(this.attributes);
  }

  getDefaultSampleRateForDeviceType = (device_type) => (device_type === 'router' ? 1024 : 32);

  get snmpVersion() {
    if (!this.get('snmp_enabled')) {
      return false;
    }

    if (this.get('device_snmp_v3_conf_enabled') && this.get('device_snmp_v3_conf.UserName')) {
      return 'V3';
    }

    return 'V2';
  }

  // snmp_enabled is a derived field (set in augmentDeviceWithBasicMetadata by getDeviceStatus.js)
  // which indicates whether the device has been configured for
  // - Flow Enrichment via kProxy
  // - Flow Enrichment via Universal Agent
  // - NMS (Kentik Agent)
  @computed
  get snmpStatus() {
    return deviceDerived.snmpStatus(this.attributes);
  }

  @computed
  get stStatus() {
    return this.stEnabled && this.get('st_healthy');
  }

  @computed
  get flowConfigured() {
    return deviceDerived.flowConfigured(this.attributes);
  }

  @computed
  get nmsConfigured() {
    return deviceDerived.nmsConfigured(this.attributes);
  }

  @computed
  get snmpEnrichmentStatus() {
    return deviceDerived.snmpEnrichmentStatus(this.attributes);
  }

  @computed
  get stEnrichmentStatus() {
    return deviceDerived.stEnrichmentStatus(this.attributes);
  }

  @computed
  get snmpNmsStatus() {
    return deviceDerived.snmpNmsStatus(this.attributes);
  }

  @computed
  get stNmsStatus() {
    return deviceDerived.stNmsStatus(this.attributes);
  }

  @computed
  get icmpStatus() {
    return deviceDerived.icmpStatus(this.attributes);
  }

  @computed
  get bgpStatus() {
    return deviceDerived.bgpStatus(this.attributes);
  }

  @computed
  get bgpCombinedStatus() {
    const { v4, v6 } = this.bgpStatus;
    let status = 'not configured';

    if (v4.configured || v6.configured) {
      if (v4.established || v6.established) {
        status = 'established';
      } else {
        status = 'not established';
      }
    }

    return status;
  }

  @computed
  get bgpUpdates() {
    return {
      v4: parseInt(this.get('stats.RibUpdates')) || 0,
      v6: parseInt(this.get('stats.RibUpdates6')) || 0
    };
  }

  @computed
  get stEnabled() {
    return deviceDerived.stEnabled(this.attributes);
  }

  deviceFilter(filterField = 'i_device_id') {
    return {
      connector: 'All',
      filterGroups: [
        {
          connector: 'All',
          not: false,
          filters: [
            {
              filterField,
              operator: '=',
              filterValue: `${this.id}`
            }
          ]
        }
      ]
    };
  }

  @computed
  get isNMS() {
    return deviceDerived.isNMS(this.attributes);
  }

  // checks if the device is using only NMS kentik-agent for data collection
  // unlike isNMS, which checks for both kentik-agent and kentik-agent-flow-only
  @computed
  get consumesNmsLicense() {
    const snmpCollectionMethod = this.get('snmp_collection_method');
    if (!snmpCollectionMethod) {
      return false;
    }

    const methods = Array.isArray(snmpCollectionMethod)
      ? snmpCollectionMethod
      : String(snmpCollectionMethod)
          .split(',')
          .map((m) => m.trim());

    return methods.includes('kentik_agent') && !methods.includes('kentik_agent_flow_only');
  }

  @computed
  get isEnrichingFlowWithKproxy() {
    return this.get('snmp_collection_method')?.includes('kproxy');
  }

  @computed
  get isEnrichingFlowWithUA() {
    return deviceDerived.isEnrichingFlowWithUA(this.attributes);
  }

  @computed
  get isICMP() {
    return deviceDerived.isICMP(this.attributes);
  }

  @computed
  get isICMPOnly() {
    return deviceDerived.isICMPOnly(this.attributes);
  }

  @computed
  get hasStreamingTelemetry() {
    return deviceDerived.hasStreamingTelemetry(this.attributes);
  }

  @computed
  get hasKentikAgent() {
    return !!this.get('device_kentik_agent_id');
  }

  @computed
  get hasKproxyAgent() {
    return !!this.get('device_kproxy_agent_id');
  }

  @computed
  get canRemoteSnmpWalk() {
    // Device can do remote SNMP walks if it has a Kentik Agent with SNMP capabilities
    // This includes both full NMS (kentik_agent) and flow enrichment (kentik_agent_flow_only)
    // Check both device_snmp_ip (legacy) and ranger_snmp_ip (agent-based)
    const snmpCollectionMethod = this.get('snmp_collection_method');
    if (!snmpCollectionMethod) {
      return false;
    }

    // Handle both string (comma-separated) and array formats
    const methods = Array.isArray(snmpCollectionMethod)
      ? snmpCollectionMethod
      : String(snmpCollectionMethod)
          .split(',')
          .map((m) => m.trim());

    const hasNmsOrFlowEnrichment = methods.includes('kentik_agent') || methods.includes('kentik_agent_flow_only');

    return (
      this.hasKentikAgent && hasNmsOrFlowEnrichment && (!!this.get('device_snmp_ip') || !!this.get('ranger_snmp_ip'))
    );
  }

  @computed
  get hasCommandAccess() {
    const hasPermission = $auth.hasPermission('recon.deviceConfigFetch.enabled', { overrideForSudo: false });
    return deviceDerived.hasCommandAccess(this.attributes, hasPermission);
  }

  @computed
  get hasConfigFetch() {
    return deviceDerived.hasConfigFetch(this.attributes, this.hasCommandAccess);
  }

  @computed
  get aiAdvisorAccess() {
    return deviceDerived.aiAdvisorAccess(this.attributes, this.hasCommandAccess);
  }

  @computed
  get isConfigFetchEnabled() {
    return !!this.get('configFetchEnabled');
  }

  /**
   * Names of the credentials in use by the device
   */
  @computed
  get credentials() {
    return deviceDerived.credentials(this.attributes, this.hasCommandAccess);
  }

  resolveAgentName(agentId) {
    if (!agentId || $kentikAgents.collection.loading) {
      return null;
    }

    if (agentId.startsWith(GROUP_ID_PREFIX)) {
      return $kentikAgents.findNameByGroupId(agentId);
    }

    return $kentikAgents.collection.get(agentId)?.name ?? agentId;
  }

  // SNMP/Ranger Agent column - based on device_kvs['agent_id']
  @computed
  get agentName() {
    return this.resolveAgentName(this.get('device_kentik_agent_id'));
  }

  // Flow Proxy Agent column - based on device_kvs['kproxy_agent_id']
  @computed
  get kproxyAgentName() {
    return this.resolveAgentName(this.get('device_kproxy_agent_id'));
  }

  @computed
  get cpuUtilizationQuery() {
    return {
      ...DEVICE_CPU_QUERY_SERIES,
      kmetrics: {
        ...DEVICE_CPU_QUERY_SERIES.kmetrics,
        filters: this.deviceFilter()
      }
    };
  }

  @computed
  get memoryUtilizationQuery() {
    return {
      use_kmetrics: true,
      show_overlay: false,
      show_total_overlay: false,
      kmetrics: {
        measurement: '/system/memory',
        dimensions: ['device_name'],
        metrics: [
          {
            name: 'utilization',
            type: 'gauge'
          }
        ],
        merge: {
          aggregate: 'avg',
          dimensions: ['index']
        },
        range: {
          lookback: 'PT86400S'
        },
        window: {
          size: 0,
          fn: {
            utilization: 'avg'
          }
        },
        rollups: {
          avg_utilization: {
            metric: 'utilization',
            aggregate: 'avg'
          },
          max_utilization: {
            metric: 'utilization',
            aggregate: 'max'
          },
          last_utilization: {
            metric: 'utilization',
            aggregate: 'last'
          }
        },
        sort: [
          {
            name: 'avg_utilization',
            direction: 'desc'
          },
          {
            name: 'max_utilization',
            direction: 'desc'
          },
          {
            name: 'last_utilization',
            direction: 'desc'
          }
        ],
        limit: 5,
        includeTimeseries: 5,
        viz: {
          type: 'area',
          rollup: '',
          metric: 'utilization'
        },
        filters: this.deviceFilter()
      }
    };
  }

  @computed
  get temperatureQuery() {
    return {
      use_kmetrics: true,
      show_overlay: false,
      show_total_overlay: false,
      kmetrics: {
        measurement: '/components/temperature',
        dimensions: ['device_name'],
        metrics: [
          {
            name: 'instant',
            type: 'gauge'
          }
        ],
        range: {
          lookback: 'PT86400S'
        },
        merge: {
          dimensions: ['index'],
          aggregate: 'max'
        },
        window: {
          size: 0,
          fn: {
            instant: 'avg'
          }
        },
        rollups: {
          avg_instant: {
            metric: 'instant',
            aggregate: 'avg'
          },
          max_instant: {
            metric: 'instant',
            aggregate: 'max'
          },
          last_instant: {
            metric: 'instant',
            aggregate: 'last'
          }
        },
        sort: [
          {
            name: 'avg_instant',
            direction: 'desc'
          },
          {
            name: 'max_instant',
            direction: 'desc'
          },
          {
            name: 'last_instant',
            direction: 'desc'
          }
        ],
        limit: 5,
        includeTimeseries: 5,
        viz: {
          type: 'area',
          rollup: '',
          metric: 'instant'
        },
        filters: this.deviceFilter()
      }
    };
  }

  @computed
  get trafficQuery() {
    return {
      all_devices: false,
      device_name: [this.get('device_name')],
      lookback_seconds: 86400,
      aggregateTypes: ['avg_bits_per_sec'],
      show_overlay: false,
      show_total_overlay: false
    };
  }

  @computed
  get deviceClass() {
    return deviceDerived.deviceClass(this.attributes);
  }

  hasCapability(capability) {
    const { deviceClass } = this;
    if (!deviceClass) {
      return false;
    }
    return DeviceClassToCapabilities[deviceClass]?.includes(capability);
  }
}

export default DeviceModel;
